import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Poll } from "../types";
import { apiService } from "../services/api";
import { PollCard } from "../components/PollCard";
import { CreatePollForm } from "../components/CreatePollForm";
import { CATEGORIES } from "../constants/categories";
import "./ExplorePage.css";

interface ExplorePageProps {
  showCreateForm?: boolean;
  onShowCreateFormChange?: (show: boolean) => void;
}

export const ExplorePage: React.FC<ExplorePageProps> = ({
  showCreateForm = false,
  onShowCreateFormChange,
}) => {
  const navigate = useNavigate();
  const [polls, setPolls] = useState<Poll[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nextKey, setNextKey] = useState<string | undefined>(undefined);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category");
  const tabParam = searchParams.get("tab");

  const [showClosed, setShowClosed] = useState(() => tabParam === "closed");
  const requestIdRef = useRef(0);

  // Sync tab parameter with showClosed state
  useEffect(() => {
    setShowClosed(tabParam === "closed");
  }, [tabParam]);

  const handleCategoryClick = (category: string | null) => {
    const newParams = new URLSearchParams(searchParams);
    if (category === null) {
      newParams.delete("category");
    } else {
      newParams.set("category", category);
    }
    setSearchParams(newParams);
  };

  const handleTabChange = (closed: boolean) => {
    const newParams = new URLSearchParams(searchParams);
    if (closed) {
      newParams.set("tab", "closed");
    } else {
      newParams.delete("tab");
    }
    setSearchParams(newParams);
  };

  // Load polls when activeCategory or showClosed changes
  useEffect(() => {
    loadPolls();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory, showClosed]);


  const loadPolls = useCallback(async (append: boolean = false) => {
    const requestId = ++requestIdRef.current;
    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
      setPolls([]);
      setNextKey(undefined);
    }
    setError(null);

    try {
      const response = await apiService.listPolls(
        20,
        append ? nextKey : undefined,
        undefined,
        activeCategory || undefined,
        showClosed
      );

      if (requestId !== requestIdRef.current) {
        return;
      }

      if (append) {
        setPolls((prev) => [...prev, ...response.polls]);
      } else {
        setPolls(response.polls);
      }

      setNextKey(response.nextKey);
      setHasMore(response.hasMore);
    } catch (err: any) {
      if (requestId === requestIdRef.current) {
        setError(err.message || "Failed to load polls");
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    }
  }, [nextKey, activeCategory, showClosed]);

  const handleVote = (updatedPoll: Poll) => {
    setPolls(
      polls.map((p) => (p.pollId === updatedPoll.pollId ? updatedPoll : p))
    );
  };

  const handleDelete = (deletedPollId: string) => {
    // Remove the deleted poll from the list
    setPolls(polls.filter((p) => p.pollId !== deletedPollId));
  };

  const handlePollCreated = (createdPoll: Poll) => {
    if (onShowCreateFormChange) {
      onShowCreateFormChange(false);
    }
    // Navigate to the newly created poll in the feed
    navigate(`/feed/${createdPoll.pollId}`);
  };

  const handleLoadMore = () => {
    if (hasMore && !isLoadingMore) {
      loadPolls(true);
    }
  };

  return (
    <div className="explore-page">
      <div className="explore-container">

        <div className="category-filter-bar">
          <div className="categories-scroll">
            <button
              className={`category-btn ${activeCategory === null ? "active" : ""}`}
              onClick={() => handleCategoryClick(null)}
            >
              <span className="category-icon">🎯</span>
              <span className="category-name">All</span>
            </button>
            {Object.entries(CATEGORIES).map(([key, value]) => (
              <button
                key={key}
                className={`category-btn ${activeCategory === key ? "active" : ""}`}
                onClick={() => handleCategoryClick(key)}
              >
                <span className="category-icon">{value.icon}</span>
                <span className="category-name">{value.name}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="status-filter-container">
          <button
            className={`status-btn ${!showClosed ? "active" : ""}`}
            onClick={() => handleTabChange(false)}
          >
            Active Polls
          </button>
          <button
            className={`status-btn ${showClosed ? "active" : ""}`}
            onClick={() => handleTabChange(true)}
          >
            Closed Polls
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        {isLoading ? (
          <div className="shimmer-container">
            {[1, 2, 3, 4].map((n) => (
              <div key={n} className="shimmer-card">
                <div className="shimmer-header">
                  <div className="shimmer-avatar" />
                  <div className="shimmer-meta">
                    <div className="shimmer-line short" />
                    <div className="shimmer-line xshort" />
                  </div>
                </div>
                <div className="shimmer-title" />
                <div className="shimmer-options">
                  <div className="shimmer-option" />
                  <div className="shimmer-option" />
                </div>
              </div>
            ))}
          </div>
        ) : polls.length === 0 ? (
          <div className="empty-state">
            <div className="empty-category-emoji">
              {showClosed ? "🔒" : (activeCategory ? CATEGORIES[activeCategory]?.icon : "🎯")}
            </div>
            <h3>
              {showClosed
                ? (activeCategory ? `No closed ${CATEGORIES[activeCategory]?.name} polls` : "No closed polls yet")
                : (activeCategory ? `No active ${CATEGORIES[activeCategory]?.name} polls` : "All caught up!")}
            </h3>
            <p>
              {showClosed
                ? "Check back later or create a new poll!"
                : "You've voted on everything here! Explore closed polls to see historical results."}
            </p>
            {!showClosed && (
              <button
                className="view-closed-inline-btn"
                onClick={() => handleTabChange(true)}
              >
                Explore Closed Polls
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="polls-grid">
              {polls.map((poll) => (
                <PollCard
                  key={poll.pollId}
                  poll={poll}
                  onVote={handleVote}
                  onDelete={handleDelete}
                />
              ))}
              
              {isLoadingMore && (
                <>
                  {[1, 2].map((n) => (
                    <div key={`more-shimmer-${n}`} className="shimmer-card">
                      <div className="shimmer-header">
                        <div className="shimmer-avatar" />
                        <div className="shimmer-meta">
                          <div className="shimmer-line short" />
                          <div className="shimmer-line xshort" />
                        </div>
                      </div>
                      <div className="shimmer-title" />
                      <div className="shimmer-options">
                        <div className="shimmer-option" />
                        <div className="shimmer-option" />
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>

            {hasMore && !isLoadingMore && (
              <div className="load-more-container">
                <button
                  className="load-more-btn"
                  onClick={handleLoadMore}
                  disabled={isLoadingMore}
                >
                  Load More
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {onShowCreateFormChange && (
        <CreatePollForm
          open={showCreateForm}
          onOpenChange={onShowCreateFormChange}
          onPollCreated={handlePollCreated}
        />
      )}
    </div>
  );
};
